import {useEffect, useRef, useState} from 'react';

import {Integer} from '../types/Integer';
import {getStepsFromPixels} from '../utils/getStepsFromPixels';

type UseAvailableHeightReturn<T extends HTMLElement> = {
  availableHeightRef: React.RefObject<T | null>;
  availableHeight: Integer;
};

/**
 * Hook that calculates available height for an element based on viewport height and element's position
 * @returns Object containing the ref to attach to the element and the available height in steps
 * @example
 * const {availableHeight, ref} = useAvailableHeight<HTMLDivElement>();
 * return <Box ref={ref} height={availableHeight} />;
 */
export function useAvailableHeight<T extends HTMLElement>(): UseAvailableHeightReturn<T> {
  const availableHeightRef = useRef<T | null>(null);
  const [availableHeight, setAvailableHeight] = useState<Integer>(0);

  useEffect(function subscribeToHeightChanges() {
    const calculateHeight = () => {
      if (!availableHeightRef.current) {
        return;
      }

      const rect = availableHeightRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const remainingHeight = viewportHeight - rect.top;

      if (remainingHeight > 0) {
        const steps = getStepsFromPixels(remainingHeight);
        if (steps) {
          setAvailableHeight(steps);
        }
      }
    };

    calculateHeight();

    const resizeObserver = new ResizeObserver(calculateHeight);
    resizeObserver.observe(window.document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return {availableHeightRef, availableHeight};
}
